/**
 * Sentry Performance Monitoring Utilities
 * Web Vitals and page load tracking
 */

import { addBreadcrumb } from './sentry-utils';
import { ERROR_LEVELS } from '../../constants/sentry-constants';

// Module-level observer instances for cleanup
let lcpObserver: PerformanceObserver | null = null;
let fidObserver: PerformanceObserver | null = null;
let clsObserver: PerformanceObserver | null = null;
let pageLoadListener: (() => void) | null = null;

/**
 * Type guard for LCP entry
 */
function isLCPEntry(entry: PerformanceEntry): entry is PerformanceEntry & {
  renderTime?: number;
  loadTime?: number;
} {
  return 'renderTime' in entry || 'loadTime' in entry;
}

/**
 * Type guard for FID entry
 */
function isFIDEntry(entry: PerformanceEntry): entry is PerformanceEventTiming {
  return 'processingStart' in entry && 'startTime' in entry;
}

/**
 * Type guard for LayoutShift entry
 */
function isLayoutShiftEntry(entry: PerformanceEntry): entry is PerformanceEntry & {
  hadRecentInput?: boolean;
  value?: number;
} {
  return 'value' in entry;
}

/**
 * Track Web Vitals performance metrics
 */
function trackWebVitals(): void {
  // This is automatically handled by Sentry's BrowserTracing integration
  // But we can add custom tracking if needed
  if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
    try {
      // Track Largest Contentful Paint (LCP)
      lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length === 0) return;
        
        const lastEntry = entries[entries.length - 1];
        if (!isLCPEntry(lastEntry)) return;
        
        // Type-safe access with validation
        const lcpValue = (lastEntry.renderTime ?? lastEntry.loadTime) ?? 0;
        addBreadcrumb({
          category: 'web-vitals',
          message: 'Largest Contentful Paint',
          level: ERROR_LEVELS.INFO,
          data: {
            value: lcpValue,
            unit: 'millisecond',
          },
        });
      });
      
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // Track First Input Delay (FID)
      fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!isFIDEntry(entry)) return;
          
          // Type-safe access with validation
          if (typeof entry.processingStart === 'number' && typeof entry.startTime === 'number') {
            const fidValue = entry.processingStart - entry.startTime;
            addBreadcrumb({
              category: 'web-vitals',
              message: 'First Input Delay',
              level: ERROR_LEVELS.INFO,
              data: {
                value: fidValue,
                unit: 'millisecond',
              },
            });
          }
        });
      });
      
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Track Cumulative Layout Shift (CLS)
      let clsValue = 0;
      clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!isLayoutShiftEntry(entry)) return;
          
          // Type-safe access with validation
          if (entry.hadRecentInput !== true && typeof entry.value === 'number') {
            clsValue += entry.value;
          }
        });
        
        addBreadcrumb({
          category: 'web-vitals',
          message: 'Cumulative Layout Shift',
          level: ERROR_LEVELS.INFO,
          data: {
            value: clsValue,
            unit: 'ratio',
          },
        });
      });
      
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      // Performance Observer might not be supported in all browsers
      console.warn('[Sentry] Web Vitals tracking not available:', error);
    }
  }
}

/**
 * Track page load performance
 */
function trackPageLoad(): void {
  if (typeof window !== 'undefined' && window.performance) {
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries.length === 0) return;
    
    const navigation = navigationEntries[0];
    if (!(navigation instanceof PerformanceNavigationTiming)) return;
    
    // Type-safe access with validation
    const metrics = {
      dns: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcp: navigation.connectEnd - navigation.connectStart,
      request: navigation.responseStart - navigation.requestStart,
      response: navigation.responseEnd - navigation.responseStart,
      dom: navigation.domContentLoadedEventEnd - navigation.responseEnd,
      load: navigation.loadEventEnd - navigation.fetchStart,
    };
    
    // Track page load metrics via breadcrumbs (metrics.distribution is deprecated in Sentry v8)
    Object.entries(metrics).forEach(([key, value]) => {
      addBreadcrumb({
        category: 'performance',
        message: `Page load: ${key}`,
        level: ERROR_LEVELS.INFO,
        data: {
          metric: key,
          value,
          unit: 'millisecond',
        },
      });
    });
    
    // Add breadcrumb
    addBreadcrumb({
      category: 'performance',
      message: 'Page load completed',
      level: ERROR_LEVELS.INFO,
      data: metrics,
    });
  }
}

/**
 * Initialize performance monitoring
 * Call this once when the app starts
 */
export function initPerformanceMonitoring(): void {
  // Track Web Vitals
  trackWebVitals();
  
  // Track initial page load
  if (typeof window !== 'undefined') {
    if (document.readyState === 'complete') {
      trackPageLoad();
    } else {
      // Store listener reference for cleanup
      pageLoadListener = trackPageLoad;
      window.addEventListener('load', pageLoadListener);
    }
  }
}

/**
 * Cleanup performance monitoring
 * Disconnects all PerformanceObserver instances and removes event listeners
 * Call this when the app is being torn down or when monitoring is no longer needed
 */
export function cleanupPerformanceMonitoring(): void {
  try {
    // Disconnect PerformanceObserver instances
    if (lcpObserver) {
      lcpObserver.disconnect();
      lcpObserver = null;
    }
    if (fidObserver) {
      fidObserver.disconnect();
      fidObserver = null;
    }
    if (clsObserver) {
      clsObserver.disconnect();
      clsObserver = null;
    }

    // Remove page load event listener
    if (typeof window !== 'undefined' && pageLoadListener) {
      window.removeEventListener('load', pageLoadListener);
      pageLoadListener = null;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to cleanup performance monitoring:', error);
    }
  }
}
